import React, { useState, useEffect } from 'react';
import { ArrowLeft, Key, User, Mail, Building2, CircleDollarSign, Settings, Shield, Factory, Save, Edit3 } from 'lucide-react';
import PasswordChangeForm from './admin/PasswordChangeForm';
import { useTheme } from '../core/ui/ThemeContext';
import { useLanguage } from '../core/i18n/LanguageContext';

const SettingsView = ({ user, onBack, onUserUpdated, showSuccess, showError }) => {

  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    costCenter: user.costCenter || '',
    plant: user.plant || '',
    purchasingOrg: user.purchasingOrg || '',
    purchasingGroup: user.purchasingGroup || '',
    sapUser: user.sapUser || '',
    requestor: user.requestor || ''
  });
  const [saving, setSaving] = useState(false);
  const [fieldMappings, setFieldMappings] = useState({
    costCenter: '',
    plant: '',
    purchasingOrg: '',
    purchasingGroup: ''
  });
  const [currentUserData, setCurrentUserData] = useState(user);
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  // Load fresh user data from server on mount
  useEffect(() => {
    const loadUserProfile = async () => {
      if (!user?.token) return;

      try {
        const response = await fetch('/api/auth/user/profile', {
          headers: {
            'Authorization': `Bearer ${user.token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          const updatedUser = { ...user, ...data };
          setCurrentUserData(updatedUser);

          // Update edit form with fresh data
          setEditForm({
            costCenter: data.costCenter || '',
            plant: data.plant || '',
            purchasingOrg: data.purchasingOrg || '',
            purchasingGroup: data.purchasingGroup || '',
            sapUser: data.sapUser || '',
            requestor: data.requestor || ''
          });

          // Also update parent if data changed
          if (onUserUpdated && (
            data.costCenter !== user.costCenter ||
            data.plant !== user.plant ||
            data.purchasingOrg !== user.purchasingOrg
          )) {
            onUserUpdated(updatedUser);
          }
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
      }
    };

    loadUserProfile();
  }, [user?.token]);

  // Load all field mapping names
  useEffect(() => {
    const loadMappingName = async (mappingType, code) => {
      if (!code || !user?.token) return '';

      try {
        const response = await fetch(`/api/admin/mappings/${mappingType}/lookup/${code}`, {
          headers: {
            'Authorization': `Bearer ${user.token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          return data.name || '';
        }
        return '';
      } catch (error) {
        console.error(`Error loading ${mappingType} mapping:`, error);
        return '';
      }
    };

    const loadAllMappings = async () => {
      const [costCenterName, plantName, purchasingOrgName, purchasingGroupName] = await Promise.all([
        loadMappingName('cost_center', currentUserData?.costCenter),
        loadMappingName('plant', currentUserData?.plant),
        loadMappingName('purchasing_org', currentUserData?.purchasingOrg),
        loadMappingName('purchasing_group', currentUserData?.purchasingGroup)
      ]);

      setFieldMappings({
        costCenter: costCenterName,
        plant: plantName,
        purchasingOrg: purchasingOrgName,
        purchasingGroup: purchasingGroupName
      });
    };

    if (currentUserData && user?.token) {
      loadAllMappings();
    }
  }, [currentUserData?.costCenter, currentUserData?.plant, currentUserData?.purchasingOrg, currentUserData?.purchasingGroup, user?.token]);

  const handlePasswordChanged = () => {
    setShowPasswordForm(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditForm({
      costCenter: user.costCenter || '',
      plant: user.plant || '',
      purchasingOrg: user.purchasingOrg || '',
      purchasingGroup: user.purchasingGroup || '',
      sapUser: user.sapUser || '',
      requestor: user.requestor || ''
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditForm({
      costCenter: user.costCenter || '',
      plant: user.plant || '',
      purchasingOrg: user.purchasingOrg || '',
      purchasingGroup: user.purchasingGroup || '',
      sapUser: user.sapUser || '',
      requestor: user.requestor || ''
    });
  };

  const handleSave = async () => {
    // Validate that no fields are empty
    if (!editForm.costCenter || !editForm.plant || !editForm.purchasingOrg) {
      if (showError) {
        showError(t('settings.requiredFields'));
      }
      return;
    }

    // Trim whitespace from all fields
    const trimmedForm = {
      costCenter: editForm.costCenter.trim(),
      plant: editForm.plant.trim(),
      purchasingOrg: editForm.purchasingOrg.trim(),
      purchasingGroup: editForm.purchasingGroup.trim(),
      sapUser: editForm.sapUser.trim(),
      requestor: editForm.requestor.trim()
    };

    // Check again after trimming
    if (!trimmedForm.costCenter || !trimmedForm.plant || !trimmedForm.purchasingOrg) {
      if (showError) {
        showError(t('settings.requiredFields'));
      }
      return;
    }

    setSaving(true);
    try {
      const response = await fetch('/api/auth/user/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({
          costCenter: trimmedForm.costCenter,
          plant: trimmedForm.plant,
          purchasingOrg: trimmedForm.purchasingOrg,
          purchasingGroup: trimmedForm.purchasingGroup,
          sapUser: trimmedForm.sapUser,
          requestor: trimmedForm.requestor
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || t('settings.saveError'));
      }

      if (showSuccess) {
        showSuccess(t('settings.profileSaved'));
      }

      // Update user context
      if (onUserUpdated) {
        onUserUpdated({
          ...user,
          costCenter: trimmedForm.costCenter,
          plant: trimmedForm.plant,
          purchasingOrg: trimmedForm.purchasingOrg,
          purchasingGroup: trimmedForm.purchasingGroup,
          sapUser: trimmedForm.sapUser,
          requestor: trimmedForm.requestor
        });
      }

      setIsEditing(false);
    } catch (error) {
      console.error('Error saving user settings:', error);
      if (showError) {
        showError(error.message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`${isDarkMode
      ? 'bg-gradient-to-br from-slate-900 via-gray-900 to-black'
      : 'bg-white'
    }`}>
      {/* Liquid Glass Header Section */}
      <div className="relative">
        {/* Liquid Glass Header Background */}
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gray-900/20 border-b border-white/10 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.3)]'
          : 'bg-white border-b border-slate-200'
        }`}></div>
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-r from-blue-900/5 via-transparent to-purple-900/5'
          : ''
        }`}></div>
        
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {/* Settings Icon */}
              <div className="relative group/icon">
                <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-xl blur-sm' : 'hidden'}`}></div>
                <div className={`relative ${isDarkMode ? 'bg-gradient-to-br from-blue-600/60 via-blue-700/50 to-blue-800/60 backdrop-blur-xl shadow-[0_6px_24px_rgba(59,130,246,0.25),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20' : 'bg-brand/10'} w-10 h-10 rounded-xl flex items-center justify-center`}>
                  <Settings className={`w-5 h-5 ${isDarkMode ? 'text-white/90 drop-shadow-lg' : 'text-brand'}`} />
                  <div className={`absolute top-0.5 left-0.5 w-2.5 h-2.5 bg-white/30 rounded-full blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
                </div>
              </div>
              <div>
                <h1 className={`text-2xl font-semibold ${isDarkMode
                  ? 'bg-gradient-to-r from-white via-blue-200 to-white bg-clip-text text-transparent drop-shadow-lg'
                  : 'text-slate-900'
                }`}>{t('settings.title')}</h1>
              </div>
            </div>
            
            {/* Back Button */}
            <button
              onClick={onBack}
              className={`relative group/back overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] ${
                isDarkMode ? '' : 'bg-slate-100 hover:bg-slate-200 border border-slate-200'
              }`}
              style={isDarkMode ? {
                background: 'linear-gradient(135deg, rgba(71,85,105,0.6) 0%, rgba(75,85,99,0.6) 50%, rgba(71,85,105,0.6) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.3)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
              } : undefined}
            >
              {/* Smooth Hover Overlay — Dark only */}
              <div
                className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/back:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                style={isDarkMode ? {
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 100%)'
                } : undefined}
              ></div>

              <div className={`relative z-10 px-4 py-2 flex items-center font-medium transition-all duration-300 ${
                isDarkMode ? 'text-white drop-shadow-lg group-hover/back:drop-shadow-xl' : 'text-slate-700'
              }`}>
                <ArrowLeft className="w-4 h-4 mr-2 transition-transform duration-300 group-hover/back:scale-110" />
                {t('common.back')}
              </div>

              {/* Smooth Wave Effect — Dark only */}
              <div className={`absolute inset-0 opacity-0 group-hover/back:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                <div
                  className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/back:translate-x-full transition-transform duration-800"
                  style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)' }}
                ></div>
              </div>
            </button>
          </div>
        </div>
      </div>
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Section */}
        <div className="relative group/profile mb-6">
          {/* Card Background */}
          <div className={`absolute inset-0 ${isDarkMode
            ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 border border-white/20 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.4)]'
            : 'bg-white border border-slate-200 shadow-sm'
          } rounded-3xl`}></div>
          <div className={`absolute inset-0 ${isDarkMode
            ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
            : ''
          } rounded-3xl`}></div>
          
          
          <div className="relative p-6">
            <div className="flex items-center space-x-3 mb-6">
              {/* Profile Icon */}
              <div className="relative group/profileicon">
                <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-xl blur-sm' : 'hidden'}`}></div>
                <div className={`relative ${isDarkMode ? 'bg-gradient-to-br from-blue-600/60 via-blue-700/50 to-blue-800/60 backdrop-blur-xl shadow-[0_4px_16px_rgba(59,130,246,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20' : 'bg-brand/10'} w-10 h-10 rounded-xl flex items-center justify-center`}>
                  <User className={`w-5 h-5 ${isDarkMode ? 'text-white/90 drop-shadow-lg' : 'text-brand'}`} />
                  <div className={`absolute top-1 left-1 w-2 h-2 bg-white/30 rounded-full blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
                </div>
              </div>
              <div>
                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'}`}>{t('settings.profile')}</h2>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className={`flex items-center space-x-4 p-4 ${isDarkMode 
                ? 'bg-gray-800/30 border border-white/20' 
                : 'bg-white border border-slate-200'
              } rounded-2xl`}>
                <div className="relative group/firstnameicon">
                  <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-xl blur-sm' : 'hidden'}`}></div>
                  <div className={`relative ${isDarkMode ? 'bg-gradient-to-br from-blue-600/60 via-blue-700/50 to-blue-800/60 backdrop-blur-xl shadow-[0_4px_16px_rgba(59,130,246,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20' : 'bg-brand/10'} w-10 h-10 rounded-xl flex items-center justify-center`}>
                    <User className={`w-5 h-5 ${isDarkMode ? 'text-white/90 drop-shadow-lg' : 'text-brand'}`} />
                    <div className={`absolute top-1 left-1 w-2 h-2 bg-white/30 rounded-full blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
                  </div>
                </div>
                <div className="flex-1">
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-white/70 drop-shadow-sm' : 'text-slate-500'} uppercase tracking-wide`}>{t('settings.firstName')}</p>
                  <p className={`${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'} font-medium`}>{user.firstName || '-'}</p>
                </div>
              </div>

              <div className={`flex items-center space-x-4 p-4 ${isDarkMode 
                ? 'bg-gray-800/30 border border-white/20' 
                : 'bg-white border border-slate-200'
              } rounded-2xl`}>
                <div className="relative group/lastnameicon">
                  <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-xl blur-sm' : 'hidden'}`}></div>
                  <div className={`relative ${isDarkMode ? 'bg-gradient-to-br from-blue-600/60 via-blue-700/50 to-blue-800/60 backdrop-blur-xl shadow-[0_4px_16px_rgba(59,130,246,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20' : 'bg-brand/10'} w-10 h-10 rounded-xl flex items-center justify-center`}>
                    <User className={`w-5 h-5 ${isDarkMode ? 'text-white/90 drop-shadow-lg' : 'text-brand'}`} />
                    <div className={`absolute top-1 left-1 w-2 h-2 bg-white/30 rounded-full blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
                  </div>
                </div>
                <div className="flex-1">
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-white/70 drop-shadow-sm' : 'text-slate-500'} uppercase tracking-wide`}>{t('settings.lastName')}</p>
                  <p className={`${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'} font-medium`}>{user.lastName || '-'}</p>
                </div>
              </div>

              <div className={`flex items-center space-x-4 p-4 ${isDarkMode 
                ? 'bg-gray-800/30 border border-white/20' 
                : 'bg-white border border-slate-200'
              } rounded-2xl`}>
                <div className="relative group/usernameicon">
                  <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-xl blur-sm' : 'hidden'}`}></div>
                  <div className={`relative ${isDarkMode ? 'bg-gradient-to-br from-emerald-600/60 via-emerald-700/50 to-emerald-800/60 backdrop-blur-xl shadow-[0_4px_16px_rgba(16,185,129,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20' : 'bg-slate-100'} w-10 h-10 rounded-xl flex items-center justify-center`}>
                    <User className={`w-5 h-5 ${isDarkMode ? 'text-white/90 drop-shadow-lg' : 'text-slate-400'}`} />
                    <div className={`absolute top-1 left-1 w-2 h-2 bg-white/30 rounded-full blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
                  </div>
                </div>
                <div className="flex-1">
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-white/70 drop-shadow-sm' : 'text-slate-500'} uppercase tracking-wide`}>{t('settings.username')}</p>
                  <p className={`${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'} font-medium`}>{user.username}</p>
                </div>
              </div>

              <div className={`flex items-center space-x-4 p-4 ${isDarkMode 
                ? 'bg-gray-800/30 border border-white/20' 
                : 'bg-white border border-slate-200'
              } rounded-2xl`}>
                <div className="relative group/emailicon">
                  <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-xl blur-sm' : 'hidden'}`}></div>
                  <div className={`relative ${isDarkMode ? 'bg-gradient-to-br from-purple-600/60 via-purple-700/50 to-purple-800/60 backdrop-blur-xl shadow-[0_4px_16px_rgba(147,51,234,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20' : 'bg-brand/10'} w-10 h-10 rounded-xl flex items-center justify-center`}>
                    <Mail className={`w-5 h-5 ${isDarkMode ? 'text-white/90 drop-shadow-lg' : 'text-brand'}`} />
                    <div className={`absolute top-1 left-1 w-2 h-2 bg-white/30 rounded-full blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
                  </div>
                </div>
                <div className="flex-1">
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-white/70 drop-shadow-sm' : 'text-slate-500'} uppercase tracking-wide`}>{t('settings.email')}</p>
                  <p className={`${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'} font-medium`}>{user.email || '-'}</p>
                </div>
              </div>

              <div className={`flex items-center space-x-4 p-4 ${isDarkMode 
                ? 'bg-gray-800/30 border border-white/20' 
                : 'bg-white border border-slate-200'
              } rounded-2xl`}>
                <div className="relative group/sapusericon">
                  <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-xl blur-sm' : 'hidden'}`}></div>
                  <div className={`relative ${isDarkMode ? 'bg-gradient-to-br from-cyan-600/60 via-cyan-700/50 to-cyan-800/60 backdrop-blur-xl shadow-[0_4px_16px_rgba(14,165,233,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20' : 'bg-slate-100'} w-10 h-10 rounded-xl flex items-center justify-center`}>
                    <User className={`w-5 h-5 ${isDarkMode ? 'text-white/90 drop-shadow-lg' : 'text-slate-400'}`} />
                    <div className={`absolute top-1 left-1 w-2 h-2 bg-white/30 rounded-full blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
                  </div>
                </div>
                <div className="flex-1">
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-white/70 drop-shadow-sm' : 'text-slate-500'} uppercase tracking-wide`}>{t('settings.sapUser')}</p>
                  {isEditing ? (
                    <div className="relative mt-1">
                      <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-700/30 border border-white/20 backdrop-blur-xl' : 'hidden'} rounded-lg`}></div>
                      <input
                        type="text"
                        value={editForm.sapUser}
                        onChange={(e) => setEditForm({...editForm, sapUser: e.target.value})}
                        className={`relative w-full px-2 py-1 rounded-lg focus:outline-none transition-all duration-300 ${isDarkMode ? 'bg-transparent border-0 text-white placeholder-white/50 focus:ring-2 focus:ring-blue-400/50' : 'bg-white border border-slate-200 text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-brand/40'}`}
                        placeholder={t('settings.placeholderSapUser')}
                      />
                    </div>
                  ) : (
                    <p className={`${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'} font-medium`}>{user.sapUser || '-'}</p>
                  )}
                </div>
              </div>

              <div className={`flex items-center space-x-4 p-4 ${isDarkMode 
                ? 'bg-gray-800/30 border border-white/20' 
                : 'bg-white border border-slate-200'
              } rounded-2xl`}>
                <div className="relative group/requestoricon">
                  <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-xl blur-sm' : 'hidden'}`}></div>
                  <div className={`relative ${isDarkMode ? 'bg-gradient-to-br from-rose-600/60 via-rose-700/50 to-rose-800/60 backdrop-blur-xl shadow-[0_4px_16px_rgba(244,63,94,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20' : 'bg-slate-100'} w-10 h-10 rounded-xl flex items-center justify-center`}>
                    <User className={`w-5 h-5 ${isDarkMode ? 'text-white/90 drop-shadow-lg' : 'text-slate-400'}`} />
                    <div className={`absolute top-1 left-1 w-2 h-2 bg-white/30 rounded-full blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
                  </div>
                </div>
                <div className="flex-1">
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-white/70 drop-shadow-sm' : 'text-slate-500'} uppercase tracking-wide`}>{t('settings.requester')}</p>
                  {isEditing ? (
                    <div className="relative mt-1">
                      <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-700/30 border border-white/20 backdrop-blur-xl' : 'hidden'} rounded-lg`}></div>
                      <input
                        type="text"
                        value={editForm.requestor}
                        onChange={(e) => setEditForm({...editForm, requestor: e.target.value})}
                        className={`relative w-full px-2 py-1 rounded-lg focus:outline-none transition-all duration-300 ${isDarkMode ? 'bg-transparent border-0 text-white placeholder-white/50 focus:ring-2 focus:ring-blue-400/50' : 'bg-white border border-slate-200 text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-brand/40'}`}
                        placeholder={t('settings.placeholderRequester')}
                      />
                    </div>
                  ) : (
                    <p className={`${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'} font-medium`}>{user.requestor || '-'}</p>
                  )}
                </div>
              </div>

              <div className={`flex items-center space-x-4 p-4 ${isDarkMode 
                ? 'bg-gray-800/30 border border-white/20' 
                : 'bg-white border border-slate-200'
              } rounded-2xl`}>
                <div className="relative group/depticon">
                  <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-xl blur-sm' : 'hidden'}`}></div>
                  <div className={`relative ${isDarkMode ? 'bg-gradient-to-br from-indigo-600/60 via-indigo-700/50 to-indigo-800/60 backdrop-blur-xl shadow-[0_4px_16px_rgba(99,102,241,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20' : 'bg-slate-100'} w-10 h-10 rounded-xl flex items-center justify-center`}>
                    <Building2 className={`w-5 h-5 ${isDarkMode ? 'text-white/90 drop-shadow-lg' : 'text-slate-400'}`} />
                    <div className={`absolute top-1 left-1 w-2 h-2 bg-white/30 rounded-full blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
                  </div>
                </div>
                <div className="flex-1">
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-white/70 drop-shadow-sm' : 'text-slate-500'} uppercase tracking-wide`}>{t('settings.department')}</p>
                  <p className={`${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'} font-medium`}>{user.department || '-'}</p>
                </div>
              </div>

              <div className={`flex items-center space-x-4 p-4 ${isDarkMode 
                ? 'bg-gray-800/30 border border-white/20' 
                : 'bg-white border border-slate-200'
              } rounded-2xl`}>
                <div className="relative group/costicon">
                  <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-xl blur-sm' : 'hidden'}`}></div>
                  <div className={`relative ${isDarkMode ? 'bg-gradient-to-br from-amber-600/60 via-amber-700/50 to-amber-800/60 backdrop-blur-xl shadow-[0_4px_16px_rgba(245,158,11,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20' : 'bg-brand/10'} w-10 h-10 rounded-xl flex items-center justify-center`}>
                    <CircleDollarSign className={`w-5 h-5 ${isDarkMode ? 'text-white/90 drop-shadow-lg' : 'text-brand'}`} />
                    <div className={`absolute top-1 left-1 w-2 h-2 bg-white/30 rounded-full blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
                  </div>
                </div>
                <div className="flex-1">
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-white/70 drop-shadow-sm' : 'text-slate-500'} uppercase tracking-wide`}>{t('settings.costCenter')} *</p>
                  {isEditing ? (
                    <div className="relative mt-1">
                      <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-700/30 border border-white/20 backdrop-blur-xl' : 'hidden'} rounded-lg`}></div>
                      <input
                        type="text"
                        value={editForm.costCenter}
                        onChange={(e) => setEditForm({...editForm, costCenter: e.target.value})}
                        className={`relative w-full px-2 py-1 rounded-lg focus:outline-none transition-all duration-300 ${isDarkMode ? 'bg-transparent border-0 text-white placeholder-white/50 focus:ring-2 focus:ring-blue-400/50' : 'bg-white border border-slate-200 text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-brand/40'}`}
                        placeholder={t('settings.placeholderCostCenter')}
                      />
                    </div>
                  ) : (
                    <p className={`${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'} font-medium`}>
                      {currentUserData.costCenter || '-'}{fieldMappings.costCenter && ` / ${fieldMappings.costCenter}`}
                    </p>
                  )}
                </div>
              </div>

              <div className={`flex items-center space-x-4 p-4 ${isDarkMode
                ? 'bg-gray-800/30 border border-white/20'
                : 'bg-white border border-slate-200'
              } rounded-2xl`}>
                <div className="relative group/planticon">
                  <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-xl blur-sm' : 'hidden'}`}></div>
                  <div className={`relative ${isDarkMode ? 'bg-gradient-to-br from-green-600/60 via-green-700/50 to-green-800/60 backdrop-blur-xl shadow-[0_4px_16px_rgba(34,197,94,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20' : 'bg-brand/10'} w-10 h-10 rounded-xl flex items-center justify-center`}>
                    <Factory className={`w-5 h-5 ${isDarkMode ? 'text-white/90 drop-shadow-lg' : 'text-brand'}`} />
                    <div className={`absolute top-1 left-1 w-2 h-2 bg-white/30 rounded-full blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
                  </div>
                </div>
                <div className="flex-1">
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-white/70 drop-shadow-sm' : 'text-slate-500'} uppercase tracking-wide`}>{t('settings.plant')} *</p>
                  {isEditing ? (
                    <div className="relative mt-1">
                      <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-700/30 border border-white/20 backdrop-blur-xl' : 'hidden'} rounded-lg`}></div>
                      <input
                        type="text"
                        value={editForm.plant}
                        onChange={(e) => setEditForm({...editForm, plant: e.target.value})}
                        className={`relative w-full px-2 py-1 rounded-lg focus:outline-none transition-all duration-300 ${isDarkMode ? 'bg-transparent border-0 text-white placeholder-white/50 focus:ring-2 focus:ring-blue-400/50' : 'bg-white border border-slate-200 text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-brand/40'}`}
                        placeholder={t('settings.placeholderPlant')}
                      />
                    </div>
                  ) : (
                    <p className={`${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'} font-medium`}>
                      {currentUserData.plant || '-'}{fieldMappings.plant && ` / ${fieldMappings.plant}`}
                    </p>
                  )}
                </div>
              </div>

              <div className={`flex items-center space-x-4 p-4 ${isDarkMode
                ? 'bg-gray-800/30 border border-white/20'
                : 'bg-white border border-slate-200'
              } rounded-2xl`}>
                <div className="relative group/orgicon">
                  <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-xl blur-sm' : 'hidden'}`}></div>
                  <div className={`relative ${isDarkMode ? 'bg-gradient-to-br from-purple-600/60 via-purple-700/50 to-purple-800/60 backdrop-blur-xl shadow-[0_4px_16px_rgba(147,51,234,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20' : 'bg-slate-100'} w-10 h-10 rounded-xl flex items-center justify-center`}>
                    <Building2 className={`w-5 h-5 ${isDarkMode ? 'text-white/90 drop-shadow-lg' : 'text-slate-400'}`} />
                    <div className={`absolute top-1 left-1 w-2 h-2 bg-white/30 rounded-full blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
                  </div>
                </div>
                <div className="flex-1">
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-white/70 drop-shadow-sm' : 'text-slate-500'} uppercase tracking-wide`}>{t('settings.purchasingOrg')} *</p>
                  {isEditing ? (
                    <div className="relative mt-1">
                      <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-700/30 border border-white/20 backdrop-blur-xl' : 'hidden'} rounded-lg`}></div>
                      <input
                        type="text"
                        value={editForm.purchasingOrg}
                        onChange={(e) => setEditForm({...editForm, purchasingOrg: e.target.value})}
                        className={`relative w-full px-2 py-1 rounded-lg focus:outline-none transition-all duration-300 ${isDarkMode ? 'bg-transparent border-0 text-white placeholder-white/50 focus:ring-2 focus:ring-blue-400/50' : 'bg-white border border-slate-200 text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-brand/40'}`}
                        placeholder={t('settings.placeholderPurchasingOrg')}
                      />
                    </div>
                  ) : (
                    <p className={`${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'} font-medium`}>
                      {currentUserData.purchasingOrg || '-'}{fieldMappings.purchasingOrg && ` / ${fieldMappings.purchasingOrg}`}
                    </p>
                  )}
                </div>
              </div>

              <div className={`flex items-center space-x-4 p-4 ${isDarkMode
                ? 'bg-gray-800/30 border border-white/20'
                : 'bg-white border border-slate-200'
              } rounded-2xl`}>
                <div className="relative group/purchasinggroupicon">
                  <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-xl blur-sm' : 'hidden'}`}></div>
                  <div className={`relative ${isDarkMode ? 'bg-gradient-to-br from-teal-600/60 via-teal-700/50 to-teal-800/60 backdrop-blur-xl shadow-[0_4px_16px_rgba(20,184,166,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20' : 'bg-slate-100'} w-10 h-10 rounded-xl flex items-center justify-center`}>
                    <Building2 className={`w-5 h-5 ${isDarkMode ? 'text-white/90 drop-shadow-lg' : 'text-slate-400'}`} />
                    <div className={`absolute top-1 left-1 w-2 h-2 bg-white/30 rounded-full blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
                  </div>
                </div>
                <div className="flex-1">
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-white/70 drop-shadow-sm' : 'text-slate-500'} uppercase tracking-wide`}>{t('settings.purchasingGroup')}</p>
                  {isEditing ? (
                    <div className="relative mt-1">
                      <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-700/30 border border-white/20 backdrop-blur-xl' : 'hidden'} rounded-lg`}></div>
                      <input
                        type="text"
                        value={editForm.purchasingGroup}
                        onChange={(e) => setEditForm({...editForm, purchasingGroup: e.target.value})}
                        className={`relative w-full px-2 py-1 rounded-lg focus:outline-none transition-all duration-300 ${isDarkMode ? 'bg-transparent border-0 text-white placeholder-white/50 focus:ring-2 focus:ring-blue-400/50' : 'bg-white border border-slate-200 text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-brand/40'}`}
                        placeholder="z.B. 001"
                      />
                    </div>
                  ) : (
                    <p className={`${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'} font-medium`}>
                      {currentUserData.purchasingGroup || '-'}{fieldMappings.purchasingGroup && ` / ${fieldMappings.purchasingGroup}`}
                    </p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Edit/Save/Cancel Buttons */}
            <div className={`flex justify-end space-x-3 mt-6 pt-4 border-t ${isDarkMode ? 'border-white/20' : 'border-slate-200'}`}>
              {!isEditing ? (
                <button
                  onClick={handleEdit}
                  className={`relative group/edit overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] ${isDarkMode ? '' : 'bg-brand hover:bg-[#1D4ED8] text-white px-4 py-2 flex items-center font-medium'}`}
                  style={isDarkMode ? {
                    background: 'linear-gradient(135deg, rgba(59,130,246,0.6) 0%, rgba(37,99,235,0.6) 50%, rgba(29,78,216,0.6) 100%)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    boxShadow: '0 8px 32px rgba(59,130,246,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                  } : undefined}
                >
                  {isDarkMode ? (
                    <div className="relative z-10 px-4 py-2 flex items-center font-medium text-white transition-all duration-300 drop-shadow-lg">
                      <Edit3 className="w-4 h-4 mr-2" />
                      {t('settings.editProfile')}
                    </div>
                  ) : (
                    <>
                      <Edit3 className="w-4 h-4 mr-2" />
                      {t('settings.editProfile')}
                    </>
                  )}
                </button>
              ) : (
                <>
                  <button
                    onClick={handleCancel}
                    disabled={saving}
                    className={`relative group/cancel overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 ${isDarkMode ? '' : 'bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 flex items-center font-medium border border-slate-200'}`}
                    style={isDarkMode ? {
                      background: 'linear-gradient(135deg, rgba(71,85,105,0.6) 0%, rgba(75,85,99,0.6) 50%, rgba(71,85,105,0.6) 100%)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255,255,255,0.3)',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                    } : undefined}
                  >
                    {isDarkMode ? (
                      <div className="relative z-10 px-4 py-2 flex items-center font-medium text-white drop-shadow-lg">
                        {t('settings.cancelEdit')}
                      </div>
                    ) : t('settings.cancelEdit')}
                  </button>

                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className={`relative group/save overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 ${isDarkMode ? '' : 'bg-green-600 hover:bg-green-700 text-white px-4 py-2 flex items-center font-medium'}`}
                    style={isDarkMode ? {
                      background: 'linear-gradient(135deg, rgba(16,185,129,0.7) 0%, rgba(5,150,105,0.7) 50%, rgba(4,120,87,0.7) 100%)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255,255,255,0.3)',
                      boxShadow: '0 8px 32px rgba(16,185,129,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                    } : undefined}
                  >
                    {isDarkMode ? (
                      <div className="relative z-10 px-4 py-2 flex items-center font-medium text-white drop-shadow-lg">
                        {saving ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                            {t('settings.saveChanges')}...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            {t('settings.saveChanges')}
                          </>
                        )}
                      </div>
                    ) : (
                      saving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                          {t('settings.saveChanges')}...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          {t('settings.saveChanges')}
                        </>
                      )
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Security Section */}
        <div className="relative group/security">
          {/* Card Background */}
          <div className={`absolute inset-0 ${isDarkMode
            ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 border border-white/20 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.4)]'
            : 'bg-white border border-slate-200 shadow-sm'
          } rounded-3xl`}></div>
          <div className={`absolute inset-0 ${isDarkMode
            ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
            : ''
          } rounded-3xl`}></div>
          
          
          <div className="relative p-6">
            <div className="flex items-center space-x-3 mb-6">
              {/* Security Icon */}
              <div className="relative group/securityicon">
                <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-xl blur-sm' : 'hidden'}`}></div>
                <div className={`relative ${isDarkMode ? 'bg-gradient-to-br from-amber-600/60 via-amber-700/50 to-amber-800/60 backdrop-blur-xl shadow-[0_4px_16px_rgba(245,158,11,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20' : 'bg-brand/10'} w-10 h-10 rounded-xl flex items-center justify-center`}>
                  <Shield className={`w-5 h-5 ${isDarkMode ? 'text-white/90 drop-shadow-lg' : 'text-brand'}`} />
                  <div className={`absolute top-1 left-1 w-2 h-2 bg-white/30 rounded-full blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
                </div>
              </div>
              <div>
                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'}`}>{t('settings.security')}</h2>
              </div>
            </div>
            
            {!showPasswordForm ? (
              <div className="relative group/passwordbox">
                {/* Background */}
                <div className={`absolute inset-0 ${isDarkMode
                  ? 'bg-gradient-to-br from-amber-600/20 via-yellow-600/20 to-amber-700/20 border border-amber-400/30 backdrop-blur-xl'
                  : 'bg-white border border-slate-200'
                } rounded-2xl`}></div>
                <div className={`absolute inset-0 ${isDarkMode
                  ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
                  : ''
                } rounded-2xl`}></div>
                
                <div className="relative p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {/* Key Icon */}
                      <div className="relative group/keyicon">
                        <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-xl blur-sm' : 'hidden'}`}></div>
                        <div className={`relative ${isDarkMode ? 'bg-gradient-to-br from-amber-600/60 via-amber-700/50 to-amber-800/60 backdrop-blur-xl shadow-[0_4px_16px_rgba(245,158,11,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20' : 'bg-brand/10'} w-8 h-8 rounded-xl flex items-center justify-center`}>
                          <Key className={`w-4 h-4 ${isDarkMode ? 'text-white/90 drop-shadow-lg' : 'text-brand'}`} />
                          <div className={`absolute top-1 left-1 w-2 h-2 bg-white/30 rounded-full blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
                        </div>
                      </div>
                      <div>
                        <p className={`font-medium ${isDarkMode ? 'text-amber-200 drop-shadow-lg' : 'text-slate-900'}`}>{t('settings.changePassword')}</p>
                      </div>
                    </div>
                    
                    {/* Password Change Button */}
                    <button
                      onClick={() => setShowPasswordForm(true)}
                      className={`relative group/pwbtn overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] ${isDarkMode ? '' : 'bg-brand hover:bg-[#1D4ED8] text-white px-4 py-2 flex items-center font-medium'}`}
                      style={isDarkMode ? {
                        background: 'linear-gradient(135deg, rgba(245,158,11,0.7) 0%, rgba(217,119,6,0.7) 50%, rgba(180,83,9,0.7) 100%)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255,255,255,0.3)',
                        boxShadow: '0 8px 32px rgba(245,158,11,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                      } : undefined}
                    >
                      {/* Smooth Hover Overlay — Dark only */}
                      <div
                        className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/pwbtn:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                        style={isDarkMode ? {
                          background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 100%)'
                        } : undefined}
                      ></div>

                      {isDarkMode ? (
                        <div className="relative z-10 px-4 py-2 flex items-center font-medium text-white transition-all duration-300 drop-shadow-lg group-hover/pwbtn:drop-shadow-xl">
                          <Key className="w-4 h-4 mr-2 transition-transform duration-300 group-hover/pwbtn:scale-110" />
                          {t('settings.changeBtn')}
                        </div>
                      ) : (
                        <>
                          <Key className="w-4 h-4 mr-2" />
                          {t('settings.changeBtn')}
                        </>
                      )}

                      {/* Smooth Wave Effect — Dark only */}
                      <div className={`absolute inset-0 opacity-0 group-hover/pwbtn:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                        <div
                          className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/pwbtn:translate-x-full transition-transform duration-800"
                          style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)' }}
                        ></div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="relative group/formbox">
                {/* Background */}
                <div className={`absolute inset-0 ${isDarkMode
                  ? 'bg-gradient-to-br from-blue-600/20 via-indigo-600/20 to-blue-700/20 border border-blue-400/30 backdrop-blur-xl'
                  : 'bg-white border border-slate-200'
                } rounded-2xl`}></div>
                <div className={`absolute inset-0 ${isDarkMode
                  ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
                  : ''
                } rounded-2xl`}></div>
                
                <div className="relative p-4">
                  <PasswordChangeForm
                    user={user}
                    adminUser={user}
                    onPasswordChanged={handlePasswordChanged}
                    onUserUpdated={onUserUpdated}
                    onCancel={() => setShowPasswordForm(false)}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsView;